import React, { useState } from "react";
import { TrendingUp, DollarSign, Clock, ClipboardList, TriangleAlert } from "lucide-react";
import { useDecisionsAnalyticsQuery, type AnalyticsMetrics } from "@/hooks/queries";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { cn } from "@/lib/utils";

const MetricCard: React.FC<{
  title: string;
  value: string | number;
  subtitle?: string;
  icon: React.ReactNode;
  color?: string;
}> = ({ title, value, subtitle, icon, color = "#1976d2" }) => (
  <Card className="h-full">
    <CardContent className="pt-6">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="mb-1 text-sm text-muted-foreground">{title}</p>
          <p className="text-3xl font-semibold" style={{ color }}>
            {value}
          </p>
          {subtitle && <p className="mt-2 text-sm text-muted-foreground">{subtitle}</p>}
        </div>
        <div
          className="flex items-center justify-center rounded-lg p-2"
          style={{ backgroundColor: `${color}26` }}
        >
          <span className="[&_svg]:h-7 [&_svg]:w-7" style={{ color }}>
            {icon}
          </span>
        </div>
      </div>
    </CardContent>
  </Card>
);

const ProgressBar: React.FC<{
  label: string;
  value: number;
  color: string;
}> = ({ label, value, color }) => (
  <div className="mb-4">
    <div className="mb-1 flex justify-between text-sm">
      <span className="font-medium">{label}</span>
      <span className="text-muted-foreground">{value}%</span>
    </div>
    <div className="h-2 overflow-hidden rounded-full bg-muted">
      <div
        className="h-full rounded-full transition-all"
        style={{ width: `${value}%`, backgroundColor: color }}
      />
    </div>
  </div>
);

const FALLBACK_METRICS: AnalyticsMetrics = {
  total_decisions: 156,
  approved_count: 104,
  declined_count: 52,
  approval_percentage: 67,
  decline_percentage: 33,
  total_volume_30d: 2500000,
  avg_deal_size: 45000,
  avg_decision_time: 2.5,
  top_decline_reasons: [
    { reason: "Insufficient revenue", count: 45 },
    { reason: "Too many NSFs", count: 32 },
    { reason: "High existing leverage", count: 28 },
    { reason: "Low daily balance", count: 15 },
  ],
  period_days: 30,
};

const Analytics: React.FC = () => {
  const [period, setPeriod] = useState<number>(30);
  const {
    data,
    isLoading: loading,
    isError,
    error: queryError,
  } = useDecisionsAnalyticsQuery(period);
  const metrics = data ?? (isError ? { ...FALLBACK_METRICS, period_days: period } : null);
  const error = isError ? ((queryError as Error)?.message ?? "Failed to load analytics") : null;

  const formatCurrency = (value: number): string => {
    if (value >= 1000000) {
      return `$${(value / 1000000).toFixed(1)}M`;
    }
    if (value >= 1000) {
      return `$${(value / 1000).toFixed(0)}K`;
    }
    return `$${value.toLocaleString()}`;
  };

  if (loading && !metrics) {
    return (
      <div>
        <div className="mb-8 flex items-center justify-between">
          <div>
            <Skeleton className="mb-2 h-9 w-44" />
            <Skeleton className="h-6 w-72" />
          </div>
          <Skeleton className="h-10 w-[150px] rounded-md" />
        </div>
        <div className="mb-8 grid grid-cols-1 gap-6 sm:grid-cols-2 md:grid-cols-4">
          {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
            <Card key={i} className="h-full">
              <CardContent className="pt-6">
                <Skeleton className="mb-2 h-5 w-[70%]" />
                <Skeleton className="h-9 w-1/2" />
              </CardContent>
            </Card>
          ))}
        </div>
        <div className="rounded-lg border bg-card p-6">
          <Skeleton className="h-80 w-full rounded-md" />
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-8 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <h1 className="text-3xl font-semibold tracking-tight">Analytics</h1>
        <div className="flex flex-col gap-2 sm:w-[180px]">
          <Label htmlFor="analytics-period">Time period</Label>
          <Select value={String(period)} onValueChange={(v) => setPeriod(Number(v))}>
            <SelectTrigger id="analytics-period">
              <SelectValue placeholder="Period" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="7">Last 7 days</SelectItem>
              <SelectItem value="30">Last 30 days</SelectItem>
              <SelectItem value="90">Last 90 days</SelectItem>
              <SelectItem value="365">Last year</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {error && (
        <Alert className="mb-6 border-amber-200 bg-amber-50 text-amber-950 dark:border-amber-900 dark:bg-amber-950/30 dark:text-amber-50">
          <TriangleAlert className="h-4 w-4" />
          <AlertDescription>{error} - Showing demo data</AlertDescription>
        </Alert>
      )}

      {metrics && (
        <>
          <div className="mb-8 grid grid-cols-1 gap-6 sm:grid-cols-2 md:grid-cols-4">
            <MetricCard
              title={`Total Volume (${period} days)`}
              value={formatCurrency(metrics.total_volume_30d)}
              icon={<DollarSign />}
              color="#2e7d32"
            />
            <MetricCard
              title="Average Deal Size"
              value={formatCurrency(metrics.avg_deal_size)}
              icon={<TrendingUp />}
              color="#1976d2"
            />
            <MetricCard
              title="Time to Decision"
              value={`${metrics.avg_decision_time}h`}
              subtitle="Average processing time"
              icon={<Clock />}
              color="#ed6c02"
            />
            <MetricCard
              title="Total Decisions"
              value={metrics.total_decisions}
              subtitle={`${metrics.approved_count} approved, ${metrics.declined_count} declined`}
              icon={<ClipboardList />}
              color="#9c27b0"
            />
          </div>

          <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
            <div className="h-full rounded-lg border bg-card p-6 shadow-sm">
              <h2 className="mb-4 text-lg font-semibold">Decision Breakdown</h2>
              <div className="mt-6">
                <ProgressBar label="Approved" value={metrics.approval_percentage} color="#2e7d32" />
                <ProgressBar label="Declined" value={metrics.decline_percentage} color="#d32f2f" />
              </div>
              <Separator className="my-4" />
              <div className="flex justify-around text-center">
                <div>
                  <p className="text-xl font-semibold text-green-700 dark:text-green-400">
                    {metrics.approved_count}
                  </p>
                  <p className="text-sm text-muted-foreground">Approved</p>
                </div>
                <div>
                  <p className="text-xl font-semibold text-red-600 dark:text-red-400">
                    {metrics.declined_count}
                  </p>
                  <p className="text-sm text-muted-foreground">Declined</p>
                </div>
                <div>
                  <p className="text-xl font-semibold text-primary">
                    {metrics.approval_percentage}%
                  </p>
                  <p className="text-sm text-muted-foreground">Approval Rate</p>
                </div>
              </div>
            </div>

            <div className="h-full rounded-lg border bg-card p-6 shadow-sm">
              <div className="mb-3 flex items-center gap-2">
                <TriangleAlert className="h-5 w-5 text-amber-600" />
                <h2 className="text-lg font-semibold">Top Decline Reasons</h2>
              </div>
              <ul className="divide-y">
                {metrics.top_decline_reasons.map((reason) => (
                  <li
                    key={reason.reason}
                    className="flex items-center justify-between gap-3 py-3 first:pt-0"
                  >
                    <span className="text-sm font-medium">{reason.reason}</span>
                    <span
                      className={cn(
                        "min-w-10 rounded-md px-2 py-1 text-center text-sm font-semibold",
                        "bg-destructive/15 text-destructive"
                      )}
                    >
                      {reason.count}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div className="mt-6">
            <div className="rounded-lg border bg-card p-6 shadow-sm">
              <h2 className="mb-4 text-lg font-semibold">Performance Summary</h2>
              <div className="mt-4 grid grid-cols-2 gap-6 sm:grid-cols-4">
                <div>
                  <p className="text-sm text-muted-foreground">Conversion Rate</p>
                  <p className="text-xl font-semibold">{metrics.approval_percentage}%</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Avg. Daily Volume</p>
                  <p className="text-xl font-semibold">
                    {formatCurrency(metrics.total_volume_30d / period)}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Decisions per Day</p>
                  <p className="text-xl font-semibold">
                    {(metrics.total_decisions / period).toFixed(1)}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Efficiency Score</p>
                  <p
                    className={cn(
                      "text-xl font-semibold",
                      metrics.avg_decision_time < 3 && "text-green-700 dark:text-green-400",
                      metrics.avg_decision_time >= 3 &&
                        metrics.avg_decision_time < 6 &&
                        "text-amber-700 dark:text-amber-400",
                      metrics.avg_decision_time >= 6 && "text-muted-foreground"
                    )}
                  >
                    {metrics.avg_decision_time < 3
                      ? "Excellent"
                      : metrics.avg_decision_time < 6
                        ? "Good"
                        : "Needs Improvement"}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default Analytics;
